import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Stack, Card, Group, Text, Button, Badge, SimpleGrid } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, EmptyState, MetricCard, DataTable } from '@repletoryreach/sdk-ui/data-display';
import { useAuth } from '@repletoryreach/sdk-auth';
import { useAffiliate, useAffiliateProgramme, useAffiliateEarnings } from '../../data/useAffiliate';
import { EnrolForm } from './components/EnrolForm';
import { ShareCard } from './components/ShareCard';
import { WithdrawDrawer } from './components/WithdrawDrawer';
import { formatMinor, formatDate } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { useT } from '../../i18n/I18nProvider';
import './refer.css';
import { HelpButton } from '../../app/guides/HelpButton';

/**
 * Refer and earn: the overview.
 *
 * Four states, and the page is one of them at a time: not enrolled (what the
 * programme is, and the form), waiting for review, suspended, and active.
 *
 * Money here is rand the platform owes this partner, so it is formatted as
 * money and not as credits. The one exception is the convert-to-credits
 * option, which says so in its own words.
 */
export default function ReferPage() {
  const t = useT();
  const { user } = useAuth();
  const programme = useAffiliateProgramme();
  const me = useAffiliate();
  const earnings = useAffiliateEarnings();
  const [withdrawOpen, setWithdrawOpen] = useState(false);

  const p = programme.data?.data;
  const a = me.data;

  if (me.isLoading || programme.isLoading) {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.title')} description={t('refer.description')} actions={<HelpButton guide="account" />} />
        <Card withBorder><Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text></Card>
      </Stack>
    );
  }

  if (me.isError) {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.title')} actions={<HelpButton guide="account" />} />
        <Alert color="error" role="alert">
          {t('refer.loadFailed')}{' '}
          <Button variant="subtle" size="sm" onClick={() => void me.refetch()}>{t('action.retry')}</Button>
        </Alert>
      </Stack>
    );
  }

  // ── Not enrolled ────────────────────────────────────────────────────────
  if (!a) {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.title')} description={t('refer.description')} actions={<HelpButton guide="account" />} />
        {p && !p.enabled ? (
          <Alert color="warning">{t('refer.closed')}</Alert>
        ) : (
          <>
            <Card withBorder data-guide="refer-how">
              <Stack gap="md">
                <Text role="label">{t('refer.how.title')}</Text>
                <ol className="rr-refer-steps">
                  <li><Text role="bodySmall">{t('refer.how.step1')}</Text></li>
                  <li><Text role="bodySmall">{t('refer.how.step2')}</Text></li>
                  <li>
                    <Text role="bodySmall">
                      {t('refer.how.step3', { percent: String(p?.percent ?? 20) })}
                    </Text>
                  </li>
                  <li>
                    <Text role="bodySmall">
                      {t('refer.how.step4', {
                        minimum: formatMinor(p?.min_withdrawal_minor ?? 50000),
                        days: String(p?.clearance_days ?? 30)
                      })}
                    </Text>
                  </li>
                </ol>
              </Stack>
            </Card>
            <EnrolForm email={user?.email} termsMarkdown={p?.terms_markdown} termsVersion={p?.terms_version} />
          </>
        )}
      </Stack>
    );
  }

  // ── Waiting, or stopped ─────────────────────────────────────────────────
  if (a.status === 'pending_review') {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.title')} actions={<HelpButton guide="account" />} />
        <EmptyState title={t('refer.pending.title')} description={t('refer.pending.body')} />
      </Stack>
    );
  }

  if (a.status === 'closed') {
    return (
      <Stack gap="lg">
        <PageHeader eyebrow={t('refer.eyebrow')} title={t('refer.title')} actions={<HelpButton guide="account" />} />
        <Alert color="warning">{a.status_reason ?? t('refer.closedAccount')}</Alert>
      </Stack>
    );
  }

  const rows = (earnings.data?.data ?? []).slice(0, 8).map((e, i) => ({ ...e, _i: i }));

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('refer.eyebrow')}
        title={t('refer.title')}
        description={t('refer.activeDescription', { percent: String(a.commission_percent) })}
        actions={
          <Group gap="xs">
            <HelpButton guide="account" />
            <Button onClick={() => setWithdrawOpen(true)} disabled={a.balances.available_minor <= 0}>
              {t('refer.withdraw.open')}
            </Button>
          </Group>
        }
      />

      {a.status === 'suspended' && (
        <Alert color="warning" role="alert">
          {t('refer.suspended')}{a.status_reason ? ` ${a.status_reason}` : ''}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md" data-guide="refer-metrics">
        <MetricCard label={t('refer.metric.available')} value={formatMinor(a.balances.available_minor)} hint={t('refer.metric.availableHint')} />
        <MetricCard label={t('refer.metric.pending')} value={formatMinor(a.balances.pending_minor)} hint={t('refer.metric.pendingHint', { days: String(a.programme.clearance_days) })} />
        <MetricCard label={t('refer.metric.lifetime')} value={formatMinor(a.balances.lifetime_minor)} hint={t('refer.metric.lifetimeHint')} />
        <MetricCard label={t('refer.metric.referrals')} value={String(a.referrals_count)} hint={t('refer.metric.referralsHint')} />
      </SimpleGrid>

      {/* The three pages behind the overview (8 October 2026): two of them had no way in. */}
      <Group gap="xs" data-guide="refer-pages">
        <Link to="/refer/referrals"><Button variant="default" size="xs">{t('refer.referrals.title')}</Button></Link>
        <Link to="/refer/earnings"><Button variant="default" size="xs">{t('refer.earnings.title')}</Button></Link>
        <Link to="/refer/withdrawals"><Button variant="default" size="xs">{t('refer.withdrawals.title')}</Button></Link>
      </Group>

      {a.code && a.link && <ShareCard code={a.code} link={a.link} />}

      <Card withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Text role="label">{t('refer.recent.title')}</Text>
            <Link to="/refer/earnings"><Button variant="subtle" size="sm">{t('refer.recent.all')}</Button></Link>
          </Group>
          {earnings.isLoading ? (
            <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('common.loading')}</Text>
          ) : rows.length === 0 ? (
            <EmptyState title={t('refer.recent.emptyTitle')} description={t('refer.recent.emptyBody')} />
          ) : (
            <DataTable<Indexed<(typeof rows)[number]>>
              idAccessor="_i"
              records={rows}
              columns={[
                { accessor: 'usage_date', title: t('refer.column.day'), render: (r) => r.usage_date ? formatDate(r.usage_date) : formatDate(r.occurred_at) },
                { accessor: 'referred_name', title: t('refer.column.company'), render: (r) => r.referred_name ?? '—' },
                { accessor: 'kind', title: t('refer.column.kind'), render: (r) => <Badge>{t(`refer.kind.${r.kind}`, r.kind)}</Badge> },
                { accessor: 'amount_minor', title: t('refer.column.amount'), render: (r) => <span className="rr-refer-amount">{formatMinor(r.amount_minor)}</span> },
                { accessor: 'cleared', title: t('refer.column.clears'), render: (r) => r.cleared ? t('refer.cleared') : formatDate(r.available_at) }
              ]}
            />
          )}
        </Stack>
      </Card>

      <WithdrawDrawer
        opened={withdrawOpen}
        onClose={() => setWithdrawOpen(false)}
        availableMinor={a.balances.available_minor}
        minimumMinor={a.programme.min_withdrawal_minor}
        hasAccount={a.has_payout_account}
      />
    </Stack>
  );
}
